import React, { useEffect, useState } from "react"
import {
  Card,
  Typography,
  Button,
  message,
  Skeleton,
  Row,
  Col,
  Avatar,
  Space,
  Empty,
  Tag,
  Tooltip,
} from "antd"
import {
  PhoneOutlined,
  CarOutlined,
  FileSearchOutlined,
  SafetyCertificateOutlined,
  EyeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons"
import { useLocation, useNavigate } from "react-router-dom"
import ROUTER from "src/router"
import ClientSearchService from "src/services/ClientSearchService"

const { Text, Title, Paragraph } = Typography
const fakeData = [
  {
    customer: {
      car_info: {
        license_plate: "30A-123.45",
        brand: "Toyota",
        type: "Vios",
        vehicle_type: "Ô tô con",
      },
    },
  },
  {
    customer: {
      car_info: {
        license_plate: "29C-678.99",
        brand: "Hyundai",
        type: "Porter",
        vehicle_type: "Xe tải",
      },
    },
  },
  {
    customer: {
      car_info: {
        license_plate: "88B1-456.78",
        brand: "Honda",
        type: "Air Blade",
        vehicle_type: "Xe máy",
      },
    },
  },
]
const CertificateByPhoneNumber = () => {
  const [result, setResult] = useState([])
  const navigate = useNavigate()
  const location = useLocation()
  const { type, keyword, token } = location.state || {}

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!keyword) {
      navigate(ROUTER.MAIN_PAGE)
      return
    }

    const cached = JSON.parse(sessionStorage.getItem("searchResult") || "{}")
    if (cached.keyword === keyword && cached.type === type) {
      setResult(cached.result)
      setLoading(false)
      return
    }

    const fetchCertificates = async () => {
      setLoading(true)
      try {
        const data = await ClientSearchService.searchBySlug(keyword, {
          type,
        })

        if (Array.isArray(data?.success)) {
          setResult(data.success)
          sessionStorage.setItem(
            "searchResult",
            JSON.stringify({
              keyword,
              type,
              result: data.success,
            })
          )
        }
      } catch (error) {
        console.error("Lỗi tải dữ liệu:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchCertificates()
  }, [keyword, type, token, navigate])

  const getVehicleTypeColor = (vehicleType) => {
    const type = vehicleType?.toLowerCase() || ""
    if (type.includes("ô tô con") || type.includes("car")) return "blue"
    if (type.includes("xe tải") || type.includes("truck")) return "orange"
    if (type.includes("xe máy") || type.includes("motorcycle")) return "green"
    if (type.includes("xe khách") || type.includes("bus")) return "purple"
    return "default"
  }

  const getAvatarBgColor = (vehicleType) => {
    const color = getVehicleTypeColor(vehicleType)
    return color === "blue"
      ? "#1890ff"
      : color === "orange"
        ? "#fa8c16"
        : color === "green"
          ? "#52c41a"
          : color === "purple"
            ? "#722ed1"
            : "#d9d9d9"
  }

  if (loading) {
    return (
      <div style={{ padding: "24px", maxWidth: "1000px", margin: "0 auto", minHeight: "100vh" }}>
        <Skeleton.Avatar size={64} active style={{ display: "block", margin: "0 auto" }} />
        <Skeleton active paragraph={{ rows: 4 }} style={{ marginTop: "24px" }} />
        <Skeleton active paragraph={{ rows: 3 }} style={{ marginTop: "32px" }} />
      </div>
    )
  }

  if (!result?.length) {
    return (
      <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto", minHeight: "100vh" }}>
        <Card style={{ textAlign: "center", borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
          <Empty
            image={<FileSearchOutlined style={{ fontSize: 64, color: "#d9d9d9" }} />}
            description={
              <>
                <Title level={4} style={{ color: "#595959" }}>
                  Không tìm thấy thông tin
                </Title>
                <Paragraph style={{ color: "#8c8c8c" }}>
                  Số điện thoại này chưa có xe đăng ký bảo hiểm trên hệ thống
                </Paragraph>
              </>
            }
          >
            <Button
              type="primary"
              size="large"
              style={{ height: 48, borderRadius: 8, background: "linear-gradient(135deg, #667eea, #764ba2)" }}
              onClick={() => navigate(ROUTER.ORDER_SEAL)}
            >
              Đặt mua ấn chỉ mới
            </Button>
          </Empty>
        </Card>
      </div>
    )
  }

  return (
    <div
      style={{
        padding: "16px",
        maxWidth: "1000px",
        margin: "0 auto",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e6f0fa 100%)",
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", margin: "0 0 32px" }}>
        <Avatar
          size={72}
          icon={<FileSearchOutlined />}
          style={{ background: "linear-gradient(135deg, #667eea, #764ba2)", marginBottom: 16 }}
        />
        <Title
          level={2}
          style={{
            background: "linear-gradient(135deg, #667eea, #764ba2)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: 8,
          }}
        >
          Kết quả tra cứu
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Số điện thoại: <strong>{keyword}</strong>
        </Text>
      </div>

      {/* Summary Card */}
      <Card
        style={{
          marginBottom: 32,
          borderRadius: 16,
          border: "none",
          boxShadow: "0 6px 24px rgba(0,0,0,0.1)",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <Space align="center" size="large">
          <Avatar
            size={56}
            icon={<PhoneOutlined />}
            style={{ background: "rgba(255,255,255,0.25)" }}
          />
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, opacity: 0.95 }}>
              {keyword}
            </div>
            <div style={{ opacity: 0.85, marginTop: 4 }}>
              Tìm thấy <strong>{result.length}</strong> xe đăng ký
            </div>
          </div>
        </Space>
      </Card>

      {/* Vehicle Cards */}
      <Row gutter={[16, 24]}>
        {result.map((item, index) => {
          const car = item?.customer?.car_info || {}
          return (
            <Col xs={24} sm={12} lg={8} key={car.license_plate || index}>
              <Card
                hoverable
                style={{
                  borderRadius: 16,
                  overflow: "hidden",
                  border: "none",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  transition: "all 0.3s",
                }}
                bodyStyle={{ padding: 0 }}
                onClick={() =>
                  navigate(ROUTER.INSURANCE_CERTIFICATE, {
                    state: {
                      type: "license_plate",
                      keyword: car.license_plate,
                      from: "order",
                    },
                  })
                }
              >
                <div style={{ padding: "20px 24px 16px", background: "#fafafa" }}>
                  <Space align="center" size={16} style={{ width: "100%" }}>
                    <Avatar
                      size={56}
                      icon={<CarOutlined />}
                      style={{ backgroundColor: getAvatarBgColor(car.vehicle_type) }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
                        {car.license_plate || "—"}
                      </div>
                      <div style={{ color: "#595959", marginBottom: 8 }}>
                        {car.brand} {car.type || "—"}
                      </div>
                      <Tag
                        color={getVehicleTypeColor(car.vehicle_type)}
                        style={{ borderRadius: 12, fontSize: 12, padding: "0 10px" }}
                      >
                        {car.vehicle_type || "Khác"}
                      </Tag>
                    </div>
                  </Space>
                </div>

                <div style={{ padding: "16px 24px", background: "white", textAlign: "center" }}>
                  <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    block
                    size="large"
                    style={{
                      borderRadius: 10,
                      height: 46,
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      border: "none",
                      fontWeight: 500,
                    }}
                  >
                    Xem chi tiết ấn chỉ
                  </Button>
                </div>
              </Card>
            </Col>
          )
        })}
      </Row>

      <style jsx>{`
        .ant-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14) !important;
        }
      `}</style>
    </div>
  )
}

export default CertificateByPhoneNumber